import React from 'react';

type StateChangeHandler<StateType> = (nextState: StateType) => void;

type UseControllableStateParams<StateType> = {
  // The controlled value
  value?: StateType;
  // The change handler for the controlled value
  onChange?: StateChangeHandler<StateType>;
  // The initial value for when state is uncontrolled
  initialValue: StateType;
};

type UseControllableStateResult<StateType> = [
  StateType,
  StateChangeHandler<StateType>,
];

export const useControllableState = <StateType>(
  params: UseControllableStateParams<StateType>
): UseControllableStateResult<StateType> => {
  const [_state, _setState] = React.useState(params.initialValue);

  const _paramsValue = params.value; // Needed to make the hook eslint rule happy since it's used in the onChange dependency array
  const value = typeof _paramsValue === 'undefined' ? _state : _paramsValue;

  const _paramsOnChange = params.onChange; // Needed to make the hook eslint rule happy since it's used in the onChange dependency array
  const onChange = React.useCallback(
    (nextValue: StateType) => {
      if (typeof _paramsValue === 'undefined') {
        // Keep internal state updated if uncontrolled
        _setState(nextValue);
      }

      if (typeof _paramsOnChange !== 'undefined') {
        // Call handler if provided regardless of being controlled or not
        _paramsOnChange(nextValue);
      }
    },
    [_paramsOnChange, _paramsValue]
  );

  return [value, onChange];
};
